import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Barcode, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ScannerScanner from '@/pages/scanner/ScannerScanner';
import { normalizeTehai, tehaiError, TEHAI_HINT } from '@/lib/tehaiNo';

/**
 * 「何のため？」— 出庫・使用・廃棄・貸出で共通のブロック（2026-11-05）。
 *
 * 選び方は3つだけ: 手配番号あり / 開発案件・製作依頼 / 番号なし。
 * 最後に選んだものはこのセッションのあいだ覚えておく（次のカートに出る・直せる）。
 * スマホ（375px）でもそのまま使える大きさ（ボタン 44px 以上・数字キーパッド）。
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const PURPOSE_SESSION_KEY = 'mfg.purpose.last';

export const NO_NUMBER_REASONS = [
  { id: 'maintenance', label: '保守・修理' },
  { id: 'internal', label: '社内用' },
  { id: 'sample', label: 'サンプル' },
  { id: 'stocktake', label: '棚卸調整' },
  { id: 'other', label: 'その他' },
];

export const EMPTY_PURPOSE = {
  purpose_type: '', tehai_no: '', purpose_linked_id: '', purpose_reason: '', purpose_memo: '',
};

/** 画面側の最終チェック（サーバーと同じ決まり）。問題なければ null。 */
export function purposeError(v, { required = true } = {}) {
  const p = v || {};
  if (!p.purpose_type) {
    return required ? '何のために使うか選んでください（手配番号あり／開発案件・製作依頼／番号なし）' : null;
  }
  if (p.purpose_type === 'tehai') return tehaiError(p.tehai_no, { required: true });
  if (p.purpose_type === 'development' || p.purpose_type === 'fab') {
    return p.purpose_linked_id ? null : '開発案件または製作依頼を選んでください';
  }
  if (!p.purpose_reason) return '番号なしのときは理由を選んでください';
  if (p.purpose_reason === 'other' && !(p.purpose_memo || '').trim()) {
    return '「その他」のときはメモを入れてください';
  }
  return null;
}

export function rememberPurpose(v) {
  try { window.sessionStorage.setItem(PURPOSE_SESSION_KEY, JSON.stringify(v || {})); }
  catch { /* セッション保存が使えない端末では覚えないだけ */ }
}

export function lastPurpose() {
  try {
    const raw = window.sessionStorage.getItem(PURPOSE_SESSION_KEY);
    return raw ? { ...EMPTY_PURPOSE, ...JSON.parse(raw) } : null;
  } catch { return null; }
}

const CHOICES = [
  { id: 'tehai', label: '手配番号あり', hint: 'TECHSの受注票の番号' },
  { id: 'development', label: '開発案件・製作依頼', hint: '案件やFABを選ぶ' },
  { id: 'none', label: '番号なし', hint: '理由を選ぶ' },
];

export default function PurposeBlock({
  value, onChange, required = true, allowScan = true, testid = 'purpose',
}) {
  const v = { ...EMPTY_PURPOSE, ...(value || {}) };
  const [links, setLinks] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [lookup, setLookup] = useState(null);     // { valid, error, work_order }
  const timer = useRef(null);
  const isDev = v.purpose_type === 'development' || v.purpose_type === 'fab';

  const set = (patch) => {
    const next = { ...v, ...patch };
    rememberPurpose(next);
    onChange(next);
  };

  useEffect(() => {
    if (!isDev || links.length) return;
    Promise.all([
      axios.get(`${API}/eng/developments`).then((r) => (Array.isArray(r.data) ? r.data : []))
        .catch(() => []),
      axios.get(`${API}/engineering/fabrication`).then((r) => (Array.isArray(r.data) ? r.data : []))
        .catch(() => []),
    ]).then(([devs, fabs]) => {
      setLinks([
        ...devs.map((d) => ({ id: d.id, kind: 'development', label: `案件 ${d.title || d.id}` })),
        ...fabs.filter((f) => ['todo', 'in_progress'].includes(f.status))
          .map((f) => ({ id: f.id, kind: 'fab', label: `FAB ${f.fab_no || ''} ${f.name || ''}`.trim() })),
      ]);
    });
  }, [isDev, links.length]);

  const runLookup = useCallback((raw) => {
    const no = normalizeTehai(raw);
    if (!no) { setLookup(null); return; }
    axios.get(`${API}/mfg/tehai/lookup`, { params: { tehai_no: no } })
      .then((r) => setLookup(r.data)).catch(() => setLookup(null));
  }, []);

  const onTehai = (raw) => {
    set({ tehai_no: raw });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => runLookup(raw), 400);
  };

  const localError = tehaiError(v.tehai_no, { required: false });

  return (
    <div className="space-y-2" data-testid={`${testid}-block`}>
      <p className="text-sm font-semibold text-zinc-800">
        何のため？{required && <span className="text-rose-600"> ＊</span>}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {CHOICES.map((c) => {
          const on = c.id === 'development' ? isDev : v.purpose_type === c.id;
          return (
            <button key={c.id} type="button" data-testid={`${testid}-choice-${c.id}`}
              onClick={() => set({ purpose_type: c.id === 'development' ? 'development' : c.id })}
              className={`min-h-[48px] w-full rounded-xl border px-3 py-2 text-left transition-colors ${
                on ? 'border-teal-600 bg-teal-50 text-teal-900'
                  : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'}`}>
              <span className="block text-sm font-semibold">{c.label}</span>
              <span className="block text-[11px] text-zinc-500">{c.hint}</span>
            </button>
          );
        })}
      </div>

      {v.purpose_type === 'tehai' && (
        <div data-testid={`${testid}-tehai`}>
          <div className="flex gap-2">
            <Input value={v.tehai_no || ''} onChange={(e) => onTehai(e.target.value)}
              inputMode="numeric" pattern="[0-9]*" placeholder="例 026100105"
              className="h-12 flex-1 font-mono text-base" data-testid={`${testid}-tehai-input`} />
            {allowScan && (
              <Button type="button" variant="outline" onClick={() => setScanOpen(true)}
                className="h-12 min-w-[48px] px-3" data-testid={`${testid}-scan-btn`}>
                <Barcode size={20} />
              </Button>
            )}
          </div>
          <p className="mt-1 text-[11px] text-zinc-500">{TEHAI_HINT}（受注票の番号）</p>
          {localError && (
            <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-rose-700"
              data-testid={`${testid}-tehai-error`}>
              <WarningCircle size={14} /> {localError}
            </p>
          )}
          {!localError && lookup?.work_order && (
            <p className="mt-1 flex items-center gap-1 text-xs font-semibold text-emerald-700"
              data-testid={`${testid}-tehai-wo`}>
              <CheckCircle size={14} /> {lookup.work_order.label}
            </p>
          )}
        </div>
      )}

      {isDev && (
        <Select value={v.purpose_linked_id || ''}
          onValueChange={(id) => {
            const hit = links.find((l) => l.id === id);
            set({ purpose_linked_id: id, purpose_type: hit?.kind || 'development' });
          }}>
          <SelectTrigger className="h-12" data-testid={`${testid}-link-select`}>
            <SelectValue placeholder="案件・製作依頼を選ぶ" />
          </SelectTrigger>
          <SelectContent className="max-h-72 bg-white">
            {links.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {v.purpose_type === 'none' && (
        <div data-testid={`${testid}-reasons`}>
          <div className="flex flex-wrap gap-2">
            {NO_NUMBER_REASONS.map((r) => (
              <button key={r.id} type="button" data-testid={`${testid}-reason-${r.id}`}
                onClick={() => set({ purpose_reason: r.id })}
                className={`min-h-[44px] rounded-full border px-4 text-sm font-semibold ${
                  v.purpose_reason === r.id ? 'border-teal-600 bg-teal-50 text-teal-900'
                    : 'border-zinc-200 bg-white text-zinc-700'}`}>
                {r.label}
              </button>
            ))}
          </div>
          {v.purpose_reason === 'other' && (
            <Textarea value={v.purpose_memo || ''} rows={2}
              onChange={(e) => set({ purpose_memo: e.target.value })}
              placeholder="何に使うか書いてください（必須）"
              className="mt-2" data-testid={`${testid}-memo`} />
          )}
        </div>
      )}

      <Dialog open={scanOpen} onOpenChange={(o) => !o && setScanOpen(false)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}
          data-testid={`${testid}-scan-dialog`}>
          <DialogHeader><DialogTitle>受注票の番号をスキャン</DialogTitle></DialogHeader>
          <ScannerScanner regionId={`${testid}-scan-region`}
            onDetected={(code) => {
              const no = normalizeTehai(code);
              set({ purpose_type: 'tehai', tehai_no: no });
              runLookup(no);
              setScanOpen(false);
            }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
